import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@/components/shadcn/ui/input';
import { BoxIcon, Check, ChevronsUp, PlusIcon } from 'lucide-react';
import { Button } from '@/components/shadcn/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/ui/popover';
import {
  Command, CommandInput, CommandGroup,
  CommandEmpty, CommandItem, CommandList
} from '@/components/shadcn/ui/command';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/shadcn/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormLabel,
  FormItem,
  FormMessage,
} from '@/components/shadcn/ui/form';
import { useNavigate } from 'react-router-dom';
import { getTokenFromStorage } from '@/utils/getToken';
import { TokenPayload } from '@/components/navbar/tabs/MenuTabs';
import { jwtDecode } from 'jwt-decode';
import { Users } from '@/entities/users';
import { useColaborator } from '@/hook/useColaborator';
import { cn } from '@/lib/utils';
import { toast } from '@/components/shadcn/ui/use-toast';

type UserFormProps = {
  onUserSubmit: (data: any) => void;
  setSelectedUser: (data: any | undefined) => void;
  data2?: Users | null;
  isOpen: boolean;
  setOpen: (value: boolean) => void;
  setEdit: (value: boolean) => void;
  editing: boolean;
};

const schema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  email: z.string().email('Email inválido'),
  roleId: z
    .string()
    .or(z.number())
    .transform((val) => Number(val))
    .optional(),
  colaboratorId: z.number().min(1, 'Colaborador inválido').optional().nullable(),
});

type FormData = z.infer<typeof schema>;

export const UserForm: React.FC<UserFormProps> = ({
  onUserSubmit,
  editing,
  data2,
  isOpen,
  setOpen,
  setEdit,
  setSelectedUser,
}) => {
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      roleId: 2,
      colaboratorId: null,
    },
  });
  const [openIndicatorSelect,setOpenIndicatorSelect] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false);
  const [colaboratorId,setColaboratorId] = useState<number>();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const navigate = useNavigate();
  const {ColaboratorsUse} = useColaborator()

  useEffect(() => {
    console.log(data2);
    if (data2) {
      form.setValue('name', data2.name || '');
      form.setValue('email', data2.email || '');
      if (data2.role?.id) {
        form.setValue('roleId', data2.role.id);
      }
      form.setValue('colaboratorId', data2.colaboratorId ?? null);
    }
    // console.log(form.getValues())
  }, [data2, form]);

  useEffect(() => {
    
    const fetchTokenData = async () => {
      let token = getTokenFromStorage();
      let decodedToken: TokenPayload | undefined;

      if (token) {
        try {
          decodedToken = jwtDecode<TokenPayload>(token);
          if (decodedToken.roles.includes('admin')) {
            setIsAdmin(true);
            if (decodedToken.sub === 1) {
              setIsSuperAdmin(true);
            }
          }
        } catch (error) {
          console.error('Erro ao decodificar token:', error);
        }
      }

      if (!decodedToken) {
        setTimeout(() => {
          token = getTokenFromStorage();
          if (token) {
            try {
              decodedToken = jwtDecode<TokenPayload>(token);
              if (decodedToken.roles.includes('admin')) {
                setIsAdmin(true);
                if (decodedToken.sub === 1) {
                  setIsSuperAdmin(true);
                }
              }
            } catch (error) {
              console.error('Erro ao tentar decodificar token novamente:', error);
            }
          }
        }, 3000);
      }
    };

    fetchTokenData();
  }, []);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = form.getValues();
    onUserSubmit(data);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={() => {
        setOpen(!isOpen);
        setEdit(false);
        setSelectedUser(undefined);
      }}
    >
      <DialogTrigger>
        {isSuperAdmin ? (
          <Button
            onClick={() => navigate('/signUp')}
            variant="ghost"
            className="gap-1 mt-2 bg-indigo-50 hover:bg-indigo-100"
          >
            <PlusIcon size={12} />
            Adicionar Usuário
          </Button>
        ) : null}
      </DialogTrigger>

      <DialogContent className="w-4/5 max-w-lg">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <BoxIcon size={22} />
              {editing ? 'Editar Usuário' : 'Novo Usuário'}
            </div>
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 pt-2">
              {/* name */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome</FormLabel>
                    <FormControl>
                      <Input placeholder="Digite o nome" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* email */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="Digite o email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* roleId */}
              <FormField
                control={form.control}
                name="roleId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ID do Cargo (Role ID)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Digite o ID do cargo"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* colaboratorId (opcional) */}
              <FormField
                control={form.control}
                name="colaboratorId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ID do Colaborador</FormLabel>
                    <FormControl>
                      <Popover open={openIndicatorSelect} onOpenChange={setOpenIndicatorSelect}>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button

                              variant='outline'
                              role='combobox'
                              className={cn('w-full h-10 justify-between bg-slate-50 focus:bg-white text-muted-foreground font-normal',
                                !field.value && 'text-muted-foreground font-normal'
                              )}>
                              {field.value
                                ? ColaboratorsUse.find(
                                  (client : any) => client.id === field.value
                                )?.name
                                : 'Vincule um colaborador'
                              }
                              <ChevronsUp className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className='w-inherit p-0'>
                          <Command>
                            <CommandInput placeholder={field.value
                                ? ColaboratorsUse.find(
                                  (client : any) => client.id === field.value
                                )?.name
                                : 'Vincule um colaborador'
                              }/>
                            <CommandEmpty>Não encontrado</CommandEmpty>
                            <CommandList>
                              <CommandGroup>
                                {ColaboratorsUse.map((client) => (
                                  <CommandItem
                                    value={client.name}
                                    key={client.id}
                                    onSelect={() => {
                                      form.setValue('colaboratorId', client.id);
                                      setColaboratorId(client.id);
                                      setOpenIndicatorSelect(false);
                                    }}
                                  >
                                    <Check
                                      className={cn('mr-2 h-4 w-4',
                                        client.id === field.value
                                          ? 'opacity-100'
                                          : 'opacity-0'
                                      )}
                                    />

                                    <span>{client.name}</span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button
                type="submit"
                className="bg-indigo-700"
                onClick={() => setOpen(false)}
              >
                {editing ? 'Salvar Alterações' : 'Cadastrar Usuário'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
